import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { CheckCircle, Clock, Ban, AlertTriangle } from 'lucide-react';
import { AccountStatus } from '../../services/userManagementService';

interface AccountStatusBadgeProps {
  status: AccountStatus;
  suspendedUntil?: string | null;
}

const AccountStatusBadge: React.FC<AccountStatusBadgeProps> = ({ status, suspendedUntil }) => {
  const { t } = useTranslation();

  switch (status) {
    case 'active':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          {t('adminPanel.accountStatus.active')}
        </Badge>
      );
    
    case 'warned':
      const warnText = suspendedUntil 
        ? t('adminPanel.accountStatus.warnedUntil', { date: new Date(suspendedUntil).toLocaleDateString() })
        : t('adminPanel.accountStatus.warned');
      
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {warnText}
        </Badge>
      );
    
    case 'suspended':
      const suspendText = suspendedUntil 
        ? t('adminPanel.accountStatus.suspendedUntil', { date: new Date(suspendedUntil).toLocaleDateString() })
        : t('adminPanel.accountStatus.suspended');
      
      return (
        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
          <Clock className="h-3 w-3 mr-1" />
          {suspendText}
        </Badge>
      );
    
    case 'banned':
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          <Ban className="h-3 w-3 mr-1" />
          {t('adminPanel.accountStatus.banned')}
        </Badge>
      );
    
    default:
      return <Badge variant="outline">{t('adminPanel.accountStatus.unknown')}</Badge>;
  }
};

export default AccountStatusBadge;
